import { BasicButton, SecondCustomInput, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useProfile from "@/hooks/useProfile";
import { AccountSettingChangeEmailFieldsSchema } from "@/schemas";
import { memberService } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/accounts/settings/account/change-email",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const handleOnBack = useBackward();

	const { profile } = useProfile();
	const { mutateAsync: mutateAsyncUpdateUser } = useMutation({
		mutationFn: memberService.updateMember.bind(memberService),
	});

	const {
		control,
		handleSubmit,
		setError,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<AccountSettingChangeEmailFieldsSchema>({
		mode: "all",
		resolver: zodResolver(AccountSettingChangeEmailFieldsSchema),
	});

	const onSubmit: SubmitHandler<AccountSettingChangeEmailFieldsSchema> = (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		if (!profile) return;
		return mutateAsyncUpdateUser(
			{
				id: profile.id,
				email: data.email,
			},
			{
				onSuccess: () => {
					toast.success("Email updated");
					return handleOnBack();
				},
				onError: (err) => {
					if (err.response?.status === 500) {
						setError("email", {
							type: "validate",
							message: "This email is already registered",
						});
					}
				},
			},
		);
	};

	return (
		<div className="wrapper !p-0 min-h-full mx-auto">
			<BackAppBar />
			<form onSubmit={handleSubmit(onSubmit)} className="container">
				<Stack spacing={4}>
					<Typography fontWeight={600} fontSize={28}>
						Change email
					</Typography>
					<div>
						<Typography fontWeight={400} fontSize={14}>
							Your current email address is
						</Typography>
						<Typography fontWeight={500} fontSize={14}>
							{profile?.email ?? "-"}
						</Typography>
					</div>
					<Typography fontWeight={400} fontSize={14}>
						Enter your new email address:
					</Typography>
					<Controller
						control={control}
						name="email"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								{...field}
								type="email"
								label="Email"
								isShowErrorMessage
								errorMessage={fieldState.error?.message}
								startIcon={<MailOutlineIcon />}
							/>
						)}
					/>
				</Stack>
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper mx-auto">
						<BasicButton
							type="submit"
							variant="contained"
							color="secondary"
							className="w-full !h-12 text-sm font-semibold !shadow-none"
							disabled={!isDirty || !isValid || isSubmitting}
							loading={isSubmitting}
						>
							Update
						</BasicButton>
					</Box>
				</Box>
			</form>
		</div>
	);
}
